import type { TransformedGame } from "@/hooks/useLiveGames";

// Curated fallback for 2026 Stanley Cup Final remaining games.
// Vegas leads Carolina 2-1 entering Game 4 (June 8, 2026).
// Used when API-Sports hasn't yet posted the single-game fixture.
// Each entry uses the user's local YYYY-MM-DD as the key so it can
// merge cleanly into useAllTodaysGames.

interface CuratedFinalGame {
  id: number;            // synthetic, negative to avoid clashing with API IDs
  localDate: string;     // YYYY-MM-DD in venue local time (approximated to North America)
  matchStartUTC: string; // scheduled puck drop UTC
  homeTeam: string;
  awayTeam: string;
  homeAbbr: string;
  awayAbbr: string;
  homeLogo: string;
  awayLogo: string;
  week: string;
  ifNecessary?: boolean;
}

const VEGAS = {
  name: "Vegas Golden Knights",
  abbr: "VGK",
  logo: "https://media.api-sports.io/hockey/teams/702.png",
};
const CAR = {
  name: "Carolina Hurricanes",
  abbr: "CAR",
  logo: "https://media.api-sports.io/hockey/teams/676.png",
};

// Schedule (8pm ET starts → 00:00 UTC next day for early games, 8pm ET = 00:00 UTC)
const FINAL_GAMES: CuratedFinalGame[] = [
  {
    id: -2026001,
    localDate: "2026-06-09",
    matchStartUTC: "2026-06-10T00:00:00.000Z", // 8pm ET June 9
    homeTeam: CAR.name, awayTeam: VEGAS.name,
    homeAbbr: CAR.abbr, awayAbbr: VEGAS.abbr,
    homeLogo: CAR.logo, awayLogo: VEGAS.logo,
    week: "Final - Game 4",
  },
  {
    id: -2026002,
    localDate: "2026-06-11",
    matchStartUTC: "2026-06-12T00:00:00.000Z", // 8pm ET June 11
    homeTeam: VEGAS.name, awayTeam: CAR.name,
    homeAbbr: VEGAS.abbr, awayAbbr: CAR.abbr,
    homeLogo: VEGAS.logo, awayLogo: CAR.logo,
    week: "Final - Game 5",
  },
  {
    id: -2026003,
    localDate: "2026-06-14",
    matchStartUTC: "2026-06-15T00:00:00.000Z", // 8pm ET June 14
    homeTeam: CAR.name, awayTeam: VEGAS.name,
    homeAbbr: CAR.abbr, awayAbbr: VEGAS.abbr,
    homeLogo: CAR.logo, awayLogo: VEGAS.logo,
    week: "Final - Game 6",
    ifNecessary: true,
  },
];

export function getCuratedStanleyCupFixturesForLocalDate(
  _localDate: string
): TransformedGame[] {
  return FINAL_GAMES.filter(g => g.localDate === _localDate && !g.ifNecessary).map(g => {
    const broadcastStartUTC = new Date(
      new Date(g.matchStartUTC).getTime() - 30 * 60 * 1000
    ).toISOString();
    return {
      id: g.id,
      homeTeam: g.homeTeam,
      awayTeam: g.awayTeam,
      homeScore: 0,
      awayScore: 0,
      period: "",
      time: "",
      isLive: false,
      homeAbbr: g.homeAbbr,
      awayAbbr: g.awayAbbr,
      homeLogo: g.homeLogo,
      awayLogo: g.awayLogo,
      tvChannels: ["ESPN+", "TNT", "NHL Network", "SN", "CBC"],
      matchStartUTC: g.matchStartUTC,
      broadcastStartUTC,
      league: "NHL",
      odds: { home: 2.0, draw: 4.0, away: 2.0 },
    };
  });
}

export function hasCuratedStanleyCupFixturesForLocalDate(localDate: string): boolean {
  return FINAL_GAMES.some(g => g.localDate === localDate);
}
